import { useMemo, useState } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import useDialogDismiss from '../../shared/useDialogDismiss'
import {
	DURATION_UNITS,
	activeAgainLabel,
	earliestLastDay,
	formatDay,
	lastDayFrom
} from '../../shared/unavailability'

import Btn from '../Btn'

import styles from './style.module.scss'

/*
 * Rückfrage vor dem Pausieren eines Unterstützer:innen-Profils.
 *
 * Zwei Dinge, die vorher fehlten und beide denselben Grund haben — niemand
 * konnte absehen, was der Knopf bewirkt:
 *
 * 1. **Wie lange?** Ohne Enddatum bleibt ein Profil unsichtbar, bis jemand
 *    daran denkt, es wieder aktiv zu setzen. Mit Enddatum schaltet es sich
 *    selbst wieder frei (nächtlicher Lauf im Backend).
 * 2. **Was heißt das?** Die Folgen für Schulen und Junior Companies stehen im
 *    Fenster, nicht im Handbuch: nicht mehr auffindbar, keine neuen Anfragen,
 *    laufende Zusammenarbeiten gehen weiter.
 *
 * Angeboten werden drei Wege zum selben Wert: eine Dauer (x Tage/Wochen/
 * Monate), ein Datum, oder ausdrücklich kein Enddatum. Welcher Weg gewählt
 * wird, ist gleich — herausgegeben wird immer der **letzte Tag der
 * Abwesenheit** als reiner Tag (`2026-08-14`) oder null.
 */

/*
 * Die vier haeufigsten Faelle als ein Klick. Jede Wahl traegt ihre eigene
 * Beschriftung statt einer Zahl im Text: i18next 20 kennt `_one`/`_other` noch
 * nicht, "1 Wochen" waere die Folge.
 */
const QUICK_CHOICES = [
	{ count: 1, unit: 'weeks', label: 'pauseQuickOneWeek' },
	{ count: 2, unit: 'weeks', label: 'pauseQuickTwoWeeks' },
	{ count: 1, unit: 'months', label: 'pauseQuickOneMonth' },
	{ count: 3, unit: 'months', label: 'pauseQuickThreeMonths' }
]

const MODES = { duration: 'duration', date: 'date', open: 'open' }

export default function UnavailableDialog({ name, byAdmin = false, isSaving = false, onCancel, onConfirm }) {
	const { t } = useTranslation()

	const [mode, setMode] = useState(MODES.duration)
	const [count, setCount] = useState(2)
	const [unit, setUnit] = useState('weeks')
	const [date, setDate] = useState('')

	const backdropProps = useDialogDismiss(() => {
		if (!isSaving) onCancel()
	})

	const today = useMemo(() => new Date(), [])

	/* Der letzte Tag der Abwesenheit — je nach gewähltem Weg. */
	const until = useMemo(() => {
		if (mode === MODES.open) return null
		if (mode === MODES.date) return date || null
		return lastDayFrom(count, unit, today)
	}, [mode, date, count, unit, today])

	// Bei "kein Enddatum" ist nichts zu prüfen; sonst muss ein Tag herauskommen,
	// und der darf nicht in der Vergangenheit liegen (das Backend weist ihn
	// ohnehin ab, aber erst nach dem Klick).
	const earliest = earliestLastDay(today)
	const isValid = mode === MODES.open || Boolean(until && until >= earliest)

	const isQuickOn = choice =>
		mode === MODES.duration && Number(count) === choice.count && unit === choice.unit

	const pickQuick = choice => {
		setMode(MODES.duration)
		setCount(choice.count)
		setUnit(choice.unit)
	}

	return (
		<div className={styles.backdrop} {...backdropProps}>
			<div className={styles.dialog} role="dialog" aria-modal="true">
				<h2 className={styles.title}>{t('pauseTitle')}</h2>
				<p className={styles.lead}>{byAdmin && name ? t('pauseLeadAdmin', { name }) : t('pauseLead')}</p>

				<section className={styles.block}>
					<h3 className={styles.blockTitle}>{t('pauseHowLong')}</h3>

					<div className={styles.quick}>
						{QUICK_CHOICES.map(choice => (
							<button
								key={`${choice.count}-${choice.unit}`}
								type="button"
								className={clsx(styles.chip, isQuickOn(choice) && styles.chipOn)}
								onClick={() => pickQuick(choice)}
							>
								{t(choice.label)}
							</button>
						))}
					</div>

					<label className={styles.option}>
						<input
							type="radio"
							name="pauseMode"
							checked={mode === MODES.duration}
							onChange={() => setMode(MODES.duration)}
						/>
						<span className={styles.optionBody}>
							<span className={styles.optionLabel}>{t('pauseForDuration')}</span>
							<span className={styles.inline}>
								<input
									type="number"
									min="1"
									max="365"
									value={count}
									className={styles.number}
									onChange={event => {
										setMode(MODES.duration)
										setCount(event.target.value)
									}}
								/>
								<select
									value={unit}
									className={styles.select}
									onChange={event => {
										setMode(MODES.duration)
										setUnit(event.target.value)
									}}
								>
									{DURATION_UNITS.map(value => (
										<option key={value} value={value}>
											{t(`pauseUnit_${value}`)}
										</option>
									))}
								</select>
							</span>
						</span>
					</label>

					<label className={styles.option}>
						<input
							type="radio"
							name="pauseMode"
							checked={mode === MODES.date}
							onChange={() => setMode(MODES.date)}
						/>
						<span className={styles.optionBody}>
							<span className={styles.optionLabel}>{t('pauseUntilDate')}</span>
							<input
								type="date"
								value={date}
								min={earliest}
								className={styles.date}
								onChange={event => {
									setMode(MODES.date)
									setDate(event.target.value)
								}}
							/>
						</span>
					</label>

					<label className={styles.option}>
						<input
							type="radio"
							name="pauseMode"
							checked={mode === MODES.open}
							onChange={() => setMode(MODES.open)}
						/>
						<span className={styles.optionBody}>
							<span className={styles.optionLabel}>{t('pauseOpenEnded')}</span>
							<span className={styles.optionHint}>{t('pauseOpenEndedHint')}</span>
						</span>
					</label>
				</section>

				{/*
				 * Das Ergebnis in einem Satz. Ohne diese Zeile muss jede:r selbst
				 * ausrechnen, was "2 Wochen" heißt — und ob der genannte Tag noch
				 * zur Pause gehört oder schon der erste Tag danach ist.
				 */}
				<p className={clsx(styles.result, !isValid && styles.resultBad)}>
					{mode === MODES.open
						? t('pauseResultOpen')
						: isValid
						? t('pauseResult', { last: formatDay(until), back: activeAgainLabel(until) })
						: t('pauseResultMissing')}
				</p>

				<ul className={styles.effects}>
					<li>{t('pauseEffectSearch')}</li>
					<li>{t('pauseEffectRequests')}</li>
					<li>{t('pauseEffectRunning')}</li>
					<li>{t('pauseEffectAccess')}</li>
					<li>{byAdmin ? t('pauseEffectMailAdmin') : t('pauseEffectMail')}</li>
				</ul>

				<div className={styles.actions}>
					<Btn type="button" theme="secondary" disabled={isSaving} onClick={onCancel}>
						{t('Cancel')}
					</Btn>
					<Btn type="button" disabled={isSaving || !isValid} onClick={() => onConfirm(until)}>
						{t('setUnavailable')}
					</Btn>
				</div>
			</div>
		</div>
	)
}
